/**
 * Support Assistant Widget
 * AI-powered floating chat with conversation memory and web search
 */
import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2, 
  Minimize2,
  Search,
  Bot,
  User,
  Sparkles,
  Globe,
  Trash2,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! 👋 I'm your AI Support Assistant. I can help you with:\n\n• Any questions about our platform\n• Construction questions & estimates\n• General knowledge (I can search the web!)\n• Technical support\n\nAsk me anything!"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    // Persist session ID in localStorage
    const saved = localStorage.getItem('support_session_id');
    return saved || null;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Save session ID when it changes
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('support_session_id', sessionId);
    }
  }, [sessionId]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/support/chat`, {
        message: userMessage,
        session_id: sessionId,
        search_enabled: true,
        page_context: location.pathname
      });

      // Update session ID
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        searched: response.data.searched,
        searchQuery: response.data.search_query,
        sources: response.data.sources
      }]);
    } catch (error) {
      console.error('Support chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting. Please try again in a moment." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/support/history/${sessionId}`);
      } catch (e) {
        console.log('Clear history error:', e);
      }
    }
    setMessages([{
      role: 'assistant',
      content: "Conversation cleared! How can I help you?"
    }]);
    const newSessionId = crypto.randomUUID();
    setSessionId(newSessionId);
    localStorage.setItem('support_session_id', newSessionId);
    toast.success('Conversation cleared');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 w-14 h-14 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center text-white z-50 group animate-pulse hidden md:flex"
        data-testid="support-widget-btn"
        title="AI Support Assistant"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-ping"></span>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-24 right-6 w-[380px] bg-white rounded-2xl shadow-2xl z-50 overflow-hidden transition-all border border-gray-200 hidden md:block ${
        isMinimized ? 'h-14' : 'h-[520px]'
      }`}
      data-testid="support-widget"
    >
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-3 flex items-center justify-between cursor-pointer"
        onClick={() => isMinimized && setIsMinimized(false)}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm">AI Support Assistant</h3>
            <p className="text-white/80 text-xs flex items-center gap-1">
              <Globe className="w-3 h-3" /> Web search enabled
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={(e) => { e.stopPropagation(); clearConversation(); }}
            className="text-white/70 hover:text-white transition p-1.5 hover:bg-white/10 rounded"
            title="Clear conversation"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
            className="text-white/70 hover:text-white transition p-1.5 hover:bg-white/10 rounded"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
            className="text-white/70 hover:text-white transition p-1.5 hover:bg-white/10 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="h-[380px] overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                  <div className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white'
                    }`}>
                      {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                    </div>
                    <div className="space-y-1">
                      <div className={`rounded-2xl px-3 py-2 ${
                        msg.role === 'user' 
                          ? 'bg-blue-600 text-white rounded-br-sm' 
                          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      </div>
                      
                      {/* Search indicator */}
                      {msg.searched && (
                        <div className="flex items-center gap-1 text-xs text-indigo-600 ml-1">
                          <Search className="w-3 h-3" />
                          <span>Searched: "{msg.searchQuery}"</span>
                        </div>
                      )}
                      
                      {/* Sources */}
                      {msg.sources && msg.sources.length > 0 && (
                        <div className="flex flex-wrap gap-1 ml-1">
                          {msg.sources.slice(0, 2).map((url, i) => (
                            <a 
                              key={i}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-gray-500 hover:text-indigo-600 flex items-center gap-0.5 bg-gray-100 px-2 py-0.5 rounded"
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                              Source {i + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                      <span className="text-sm text-gray-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition placeholder-gray-400"
                data-testid="support-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition shadow-md"
                data-testid="support-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              AI-powered • Remembers context • Can search the web
            </p>
          </div>
        </>
      )}
    </div>
  );
}
